import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Network } from 'vis-network';
import { AnalysisResult } from '../types';

const ChainVisualization: React.FC<{ result: AnalysisResult }> = ({ result }) => {
  const networkRef = useRef<HTMLDivElement>(null);
  const networkInstanceRef = useRef<Network | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const animationRef = useRef<number | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [currentScale, setCurrentScale] = useState(1);

  const CATEGORY_COLORS: Record<string, string> = {
    '需求链条': '#2563EB',
    '供给链条': '#059669',
    '价格链条': '#D97706',
    '政策链条': '#7C3AED',
    '情绪链条': '#DB2777',
    '其他链条': '#6B7280',
  };

  const inferCategory = (label: string): string => {
    const lower = label.toLowerCase();
    if (lower.includes('需求')) return '需求链条';
    if (lower.includes('供给')) return '供给链条';
    if (lower.includes('价格')) return '价格链条';
    if (lower.includes('政策') || lower.includes('利率')) return '政策链条';
    if (lower.includes('情绪') || lower.includes('信心')) return '情绪链条';
    return '其他链条';
  };

  const getNodeColor = (category?: string, level?: number): string => {
    if (category && CATEGORY_COLORS[category]) {
      return CATEGORY_COLORS[category];
    }
    const fallback = ['#38BDF8', '#34D399', '#FCD34D', '#A78BFA', '#F472B6'];
    if (typeof level === 'number') {
      return fallback[level % fallback.length];
    }
    return fallback[0];
  };

  const getLinkColor = (impact: string): string => {
    switch (impact) {
      case 'positive':
        return '#10B981'; // green - 正面影响
      case 'negative':
        return '#EF4444'; // red - 负面影响
      default:
        return '#000000'; // black - 中性影响
    }
  };

  const categories = useMemo(() => {
    if (!result.animation_data?.categories || result.animation_data.categories.length === 0) {
      const inferred = new Set<string>();
      result.animation_data?.nodes.forEach((node) => {
        inferred.add(node.category || inferCategory(node.label));
      });
      return Array.from(inferred);
    }
    return result.animation_data.categories;
  }, [result]);

  const nodeCategoryMap = useMemo(() => {
    const map: Record<string, string> = {};
    result.animation_data?.nodes.forEach((node) => {
      map[node.id] = node.category || inferCategory(node.label);
    });
    return map;
  }, [result]);

  const shouldDimNode = (category: string) =>
    Boolean(categoryFilter && category !== categoryFilter);

  const shouldDimEdge = (sourceId: string, targetId: string) => {
    if (!categoryFilter) return false;
    const sourceCategory = nodeCategoryMap[sourceId];
    const targetCategory = nodeCategoryMap[targetId];
    return sourceCategory !== categoryFilter && targetCategory !== categoryFilter;
  };

  useEffect(() => {
    if (!networkRef.current || !result.animation_data) return;

    const { nodes, links } = result.animation_data;

    // 准备vis-network数据，使用后端计算的位置
    const visNodes = nodes.map((node) => {
      const category = node.category || inferCategory(node.label);
      const dimmed = shouldDimNode(category);
      return {
        id: node.id,
        label: node.label,
        level: node.level,
        x: (node.x || 0) * 1200, // 使用后端计算的位置并缩放
        y: (node.y || 0) * 800,
        fixed: {
          x: true, // 固定位置，避免自动布局
          y: true,
        },
        color: {
          background: getNodeColor(category, node.level),
          border: '#1F2937',
          borderWidth: 2,
          highlight: {
            background: '#FCD34D',
            border: '#F59E0B',
          },
        },
        font: { 
          size: 16,
          face: 'Inter, Arial',
          vadjust: -2,
          multi: true,
          color: '#1F2937'
        },
      shape: 'box',
      widthConstraint: { maximum: 180 },
      heightConstraint: { minimum: 50, maximum: 60 },
      opacity: dimmed ? 0.1 : 0.45,
      margin: { top: 12, right: 12, bottom: 12, left: 12 },
      };
    });

    const visEdges = links.map((link) => {
      const impactColor = getLinkColor(link.impact);
      const dimmed = shouldDimEdge(link.source, link.target);
      
      return {
        from: link.source,
        to: link.target,
        label: link.label,
        color: {
          color: impactColor,
          highlight: impactColor,
          opacity: dimmed ? 0.2 : 0.8,
        },
        arrows: {
          to: {
            enabled: true,
            scaleFactor: 1.2,
            type: 'arrow',
          },
        },
          font: { 
            size: 12,
            align: 'middle' as any,
            color: '#4B5563',
          },
        width: 2.2,
        opacity: dimmed ? 0.15 : 0.35,
        dashes: link.impact === 'negative',
        smooth: false,
      };
    });

    const data = {
      nodes: visNodes,
      edges: visEdges,
    };

    const options = {
      layout: {
        hierarchical: {
          enabled: false, // 禁用自动布局，使用后端计算的位置
        },
      },
      physics: {
        enabled: false, // 禁用物理引擎，使用固定位置
      },
      nodes: {
        borderWidth: 3,
        shadow: {
          enabled: true,
          color: 'rgba(0,0,0,0.3)',
          size: 10,
          x: 5,
          y: 5,
        },
      },
      edges: {
        width: 2,
        smooth: false,
      },
      interaction: {
        dragNodes: false,
        zoomView: false,
        dragView: true,
        selectConnectedEdges: true,
        hover: true,
        keyboard: {
          enabled: true,
          bindToWindow: true,
        },
      },
      configure: {
        enabled: false,
      },
    };

    const network = new Network(networkRef.current, data, options);
    networkInstanceRef.current = network;
    network.fit({ animation: { duration: 400, easingFunction: 'easeInOutQuad' } });
    const handleZoomUpdate = (params: any) => {
      if (typeof params.scale === 'number') {
        setCurrentScale(parseFloat(params.scale.toFixed(2)));
      }
    };
    network.on('zoom', handleZoomUpdate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      network.off('zoom', handleZoomUpdate);
      network.destroy();
      networkInstanceRef.current = null;
    };
  }, [result, categoryFilter, nodeCategoryMap]);

  // 动画播放逻辑
  useEffect(() => {
    if (!isPlaying || !networkInstanceRef.current || !result.animation_data?.frames) {
      return;
    }

    const frames = result.animation_data.frames;
    let step = 0;

    const animate = () => {
      if (step >= frames.length) {
        setIsPlaying(false);
        return;
      }

      const frame = frames[step];
      setCurrentStep(step);

      const { nodes, links } = result.animation_data;

      // 更新节点和边的可见性，添加动态效果
      const updatedNodes = nodes.map((node) => {
        const isVisible = frame.visible_nodes.includes(node.id);
        const isNewlyVisible = isVisible && step === frame.step;
        const category = node.category || inferCategory(node.label);
        const dimmed = shouldDimNode(category);
        
        return {
          id: node.id,
          label: node.label,
          level: node.level,
          x: (node.x || 0) * 1200,
          y: (node.y || 0) * 800,
          fixed: {
            x: true,
            y: true,
          },
          color: {
            background: getNodeColor(category, node.level),
            border: isVisible ? '#1F2937' : '#D1D5DB',
            borderWidth: isVisible ? 2 : 1,
            highlight: {
              background: '#FCD34D',
              border: '#F59E0B',
            },
          },
          font: { 
            size: isVisible ? 16 : 13,
            face: 'Inter, Arial',
            color: isVisible ? '#111827' : '#9CA3AF'
          },
          shape: 'box',
          widthConstraint: { maximum: 180 },
          heightConstraint: { minimum: 50, maximum: 60 },
          opacity: dimmed ? 0.1 : (isVisible ? 1 : 0.2),
          margin: { top: 12, right: 12, bottom: 12, left: 12 },
          scaling: {
            min: isVisible ? 1 : 0.8,
            max: isNewlyVisible ? 1.05 : 1,
          },
        };
      });

      const updatedEdges = links.map((link) => {
        const edgeKey = `${link.source}-${link.target}`;
        const isVisible = frame.visible_links.includes(edgeKey);
        const impactColor = getLinkColor(link.impact);
        const isNewlyVisible = isVisible && !frame.visible_links.slice(0, -1).includes(edgeKey);
        const dimmed = shouldDimEdge(link.source, link.target);
        
        return {
          from: link.source,
          to: link.target,
          label: link.label,
          color: {
            color: impactColor,
            highlight: impactColor,
            opacity: isVisible ? 0.9 : 0.3,
          },
          arrows: {
            to: {
              enabled: true,
              scaleFactor: isVisible ? 1.3 : 0.9,
              type: 'arrow',
            },
          },
          font: { 
            size: isVisible ? 13 : 11,
            align: 'middle' as any,
            color: '#4B5563',
          },
          width: isVisible ? (isNewlyVisible ? 3 : 2.2) : 1,
          opacity: dimmed ? 0.1 : (isVisible ? 1 : 0.2),
          dashes: link.impact === 'negative',
          smooth: false,
          animation: {
            duration: isNewlyVisible ? 300 : 0,
          },
        };
      });

      if (networkInstanceRef.current) {
        networkInstanceRef.current.setData({
          nodes: updatedNodes,
          edges: updatedEdges,
        });
      }

      step++;
      if (isPlaying && step < frames.length) {
        animationRef.current = setTimeout(() => {
          animate();
        }, 1200) as unknown as number;
      } else {
        setIsPlaying(false);
      }
    };

    animate();

    return () => {
      if (animationRef.current) {
        clearTimeout(animationRef.current);
      }
    };
  }, [isPlaying, result, categoryFilter, nodeCategoryMap]);

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const handleReset = () => {
    setIsPlaying(false);
    setCurrentStep(0);
    if (animationRef.current) {
      clearTimeout(animationRef.current);
    }
    // 重置到初始状态
    if (networkInstanceRef.current && result.animation_data) {
      const { nodes, links } = result.animation_data;
      const visNodes = nodes.map((node) => ({
        id: node.id,
        label: node.label,
        level: node.level,
        x: (node.x || 0) * 1200,
        y: (node.y || 0) * 800,
        fixed: {
          x: true,
          y: true,
        },
        color: {
          background: getNodeColor(node.category, node.level),
          border: '#1F2937',
          borderWidth: 2,
          highlight: {
            background: '#FCD34D',
            border: '#F59E0B',
          },
        },
        font: { 
          size: 16, 
          face: 'Inter, Arial',
          color: '#1F2937'
        },
        shape: 'box',
        widthConstraint: { maximum: 180 },
        heightConstraint: { minimum: 50, maximum: 60 },
        opacity: 0.3,
        margin: { top: 12, right: 12, bottom: 12, left: 12 },
      }));

      const visEdges = links.map((link) => {
        const impactColor = getLinkColor(link.impact);
        
        return {
          from: link.source,
          to: link.target,
          label: link.label,
          color: {
            color: impactColor,
            highlight: impactColor,
            opacity: 0.8,
          },
          arrows: {
            to: {
              enabled: true,
              scaleFactor: 1.2,
              type: 'arrow',
            },
          },
          font: { 
            size: 12,
            align: 'middle' as any,
            color: '#4B5563',
          },
          width: 2.2,
          opacity: 0.3,
          dashes: link.impact === 'negative',
          smooth: false,
        };
      });

      networkInstanceRef.current.setData({
        nodes: visNodes,
        edges: visEdges,
      });
      networkInstanceRef.current.fit({ animation: { duration: 400, easingFunction: 'easeInOutQuad' } });
      setCurrentScale(parseFloat(networkInstanceRef.current.getScale().toFixed(2)));
    }
  };

  const handleZoom = (direction: 'in' | 'out') => {
    if (!networkInstanceRef.current) return;
    const current = networkInstanceRef.current.getScale();
    const factor = direction === 'in' ? 1.2 : 0.8;
    const next = Math.max(0.4, Math.min(2.5, current * factor));
    networkInstanceRef.current.moveTo({ scale: next });
    setCurrentScale(parseFloat(next.toFixed(2)));
  };

  const handleFitView = () => {
    if (!networkInstanceRef.current) return;
    networkInstanceRef.current.fit({ animation: { duration: 400, easingFunction: 'easeInOutQuad' } });
    setCurrentScale(parseFloat(networkInstanceRef.current.getScale().toFixed(2)));
  };

  const impactLegend = [
    { label: '正面影响', color: getLinkColor('positive'), style: 'solid' },
    { label: '负面影响', color: getLinkColor('negative'), style: 'dashed' },
    { label: '中性影响', color: getLinkColor('neutral'), style: 'solid' },
  ];

  return (
    <div className="space-y-4">
      {/* 分类标签 */}
      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg items-center">
          <span className="text-sm font-medium text-gray-700 mr-1">链路分类（点击高亮）：</span>
          <button
            onClick={() => setCategoryFilter(null)}
            className={`px-3 py-1 text-xs font-medium rounded-full border transition ${
              categoryFilter === null ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-700 border-gray-200'
            }`}
          >
            全部
          </button>
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setCategoryFilter(categoryFilter === category ? null : category)}
              className={`px-3 py-1 text-xs font-medium rounded-full text-white transition ${
                categoryFilter === category ? 'ring-2 ring-offset-2 ring-white' : 'opacity-75'
              }`}
              style={{ backgroundColor: CATEGORY_COLORS[category] || CATEGORY_COLORS['其他链条'] }}
            >
              {category}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-gray-600">
          步骤 {currentStep + 1} / {result.animation_data?.frames?.length || 1}
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <button
            onClick={handlePlay}
            disabled={isPlaying}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            播放
          </button>
          <button
            onClick={handlePause}
            disabled={!isPlaying}
            className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            暂停
          </button>
          <button
            onClick={handleReset}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            重置
          </button>
          <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-md px-2 py-1">
            <button
              onClick={() => handleZoom('out')}
              className="px-2 py-1 text-sm text-gray-600 hover:text-gray-900"
            >
              -
            </button>
            <span className="text-xs text-gray-500 w-16 text-center">x{currentScale.toFixed(2)}</span>
            <button
              onClick={() => handleZoom('in')}
              className="px-2 py-1 text-sm text-gray-600 hover:text-gray-900"
            >
              +
            </button>
            <button
              onClick={handleFitView}
              className="px-2 py-1 text-xs text-blue-600 hover:text-blue-800"
            >
              视图
            </button>
          </div>
        </div>
      </div>

      {/* 图例说明 */}
      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 bg-gray-50 p-3 rounded">
        <span className="font-medium">图例：</span>
        {impactLegend.map((item) => (
          <div key={item.label} className="flex items-center gap-2">
            <div
              className="w-10 h-0.5"
              style={{
                backgroundColor: item.color,
                borderBottom: item.style === 'dashed' ? `2px dashed ${item.color}` : undefined,
              }}
            />
            <span>{item.label}</span>
          </div>
        ))}
        <div className="text-xs text-gray-400 ml-auto">
          * 节点按层级自上而下排列，滚动仅用于页面滚动，如需缩放请使用上方控件
        </div>
      </div>

      <div
        ref={networkRef}
        className="w-full border border-gray-300 rounded-lg bg-white"
        style={{ minHeight: '600px', height: '70vh' }}
      />
    </div>
  );
};

export default ChainVisualization;


